import "regenerator-runtime/runtime";
// import React, { useState, useEffect } from "react";
import React, { useState, useEffect } from "preact/compat";
import ReactPaginate from "react-paginate";
import Highlighter from "react-highlight-words";
import syncscroll from "sync-scroll";
import {
  IndeterminateCheckbox,
  HighlighterGlobalWithClasses,
  ArrowSVG,
} from "../lib";
import Spinner from "../svg/spinner.svg";
import Lupa from "../svg/lupa.svg";
import { CSVLink } from "react-csv";

import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
  useFilters,
  filterTypes,
} from "react-table";
import axios from "axios";

// Define a default UI for filtering
function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      type="text"
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || ""); // Set undefined to remove the filter entirely
      }}
      placeholder={`Buscar`}
    />
  );
}

function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      if (row.values[id]) {
        options.add(row.values[id]);
      }
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  if (filterValue === undefined) {
    filterValue = "";
  }

  // Render a multi-select box
  return (
    <select
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">Todos</option>
      {options.sort().map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function HighlighterGlobal({ value, state, cell }) {
  if (state.globalFilter) {
    return (
      <div>
        <Highlighter
          highlightClassName="highlight"
          searchWords={[state.globalFilter]}
          autoEscape={true}
          textToHighlight={value}
        />
      </div>
    );
  } else {
    return <>{value}</>;
  }
}

function Celula({ value, state, cell }) {
  if (state.globalFilter || state.filters.length > 0) {
    const itemFiltrado = state.filters.find(function (filtro) {
      return cell.column.id === filtro.id;
    });

    if (itemFiltrado || state.globalFilter) {
      const highlight = [];
      if (itemFiltrado) {
        highlight.push(itemFiltrado.value);
      }
      if (state.globalFilter) {
        highlight.push(state.globalFilter);
      }
      return (
        <div>
          <Highlighter
            highlightClassName="highlight"
            searchWords={highlight}
            autoEscape={true}
            textToHighlight={value}
          />
        </div>
      );
    } else {
      return <>{value}</>;
    }
  } else {
    return <>{value}</>;
  }
}

function GlobalFilter({ globalFilter, setGlobalFilter }) {
  // const onChange = useAsyncDebounce((value) => {
  //   setGlobalFilter(value || undefined);
  // }, 200);

  return (
    <div className="flex items-center flex-grow w-full pl-2 mb-4 xl:mb-0 xl:w-auto bg-cinza1">
      <Lupa />
      <input
        type="text"
        value={globalFilter || ""}
        onChange={(e) => {
          setGlobalFilter(e.target.value || undefined);
        }}
        placeholder={`Buscar`}
      />
    </div>
  );
}

function App(props) {
  const { pageLink } = props;
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showPainel, setShowPainel] = useState(false);

  function togglePainel() {
    if (!showPainel) {
      setShowPainel(true);
    } else {
      setShowPainel(false);
    }
  }

  function prepareRows(rows) {
    const output = [];
    rows.forEach((row) => {
      output.push(row.values);
    });
    return output;
  }

  function prepareHeaders() {
    const output = [];
    columns.forEach((column) => {
      output.push({ label: column.Header, key: column.accessor });
    });
    return output;
  }

  useEffect(() => {
    (async () => {
      // console.log(props.dataOrigin);
      const result = await axios(props.dataOrigin);
      setData(result.data);
      setLoaded(true);
    })();
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Nome da Empresa",
        accessor: "empresa",
        Cell: Celula,
      },
      {
        Header: "UF",
        accessor: "uf",
        Filter: SelectColumnFilter,
        filter: "includes",
        Cell: HighlighterGlobal,
      },
      {
        Header: "Nível",
        accessor: "nivel",
        Cell: Celula,
      },
      {
        Header: "Regime",
        accessor: "regime",
        Cell: Celula,
      },
      {
        Header: "Validade",
        accessor: "validade",
        Cell: Celula,
      },
      {
        Header: "OAC",
        accessor: "oc",
        Cell: Celula,
      },
      {
        Header: "Status",
        accessor: "certificado",
        Cell: HighlighterGlobalWithClasses,
        Filter: SelectColumnFilter,
        filter: "equals",
      },
    ],
    []
  );

  const handlePageClick = (data) => {
    // console.log(`active page is ${data.selected}`);
    gotoPage(data.selected);
  };

  function limparTabela() {
    setGlobalFilter(undefined);
    setAllFilters([]);
  }

  function resetarColunas() {
    const defaults = [];
    allColumns.map((column) => {
      column.toggleHidden(true);
      if (defaults.includes(column.id)) {
        column.toggleHidden(false);
      }
    });
  }

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    getToggleHideAllColumnsProps,
    headerGroups,
    prepareRow,
    allColumns,
    page,
    state,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    setGlobalFilter,
    rows,
    state: { pageIndex, pageSize, filters, globalFilter },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      defaultCanFilter: false,
      initialState: {
        pageIndex: 0,
        pageSize: 50,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (!loaded) {
    return <Spinner />;
  }

  return (
    <>
      {/* <pre>
        <code>
          {JSON.stringify(
            {
              pageIndex,
              pageSize,
              pageCount,
              canNextPage,
              canPreviousPage,
              filters,
              globalFilter,
            },
            null,
            2
          )}
        </code>
      </pre> */}
      <div className="grid gap-4 xl:grid-cols-12">
        <div className="xl:col-span-8 xl:col-start-3">
          <div
            className="flex items-center justify-between p-3 text-white transition duration-300 ease-in-out cursor-pointer painel-header bg-azulRoyal hover:bg-azulIceberg"
            onClick={() => togglePainel()}            
            onClick={() => togglePainel()}
          >
            <p>Personalizar Colunas</p>
            <svg
              className={`${
                showPainel
                  ? "transition-transform transform ease-out transform-gpu rotate-180"
                  : "transition-transform ease-out transform transform-gpu"
              }`}
              width="25"
              height="12"
              viewBox="0 0 25 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.6808 11.2538C12.9586 11.2188 13.2155 11.1136 13.4168 10.9501L24.5274 1.97858C24.8086 1.77853 24.9787 1.49086 24.9979 1.18277C25.0187 0.874661 24.8868 0.572403 24.635 0.348974C24.3816 0.124103 24.0291 -0.00147732 23.6629 -1.53685e-05C23.2948 -1.53987e-05 22.9459 0.128483 22.6942 0.354815L12.5 8.59031L2.30585 0.354814C2.05413 0.128481 1.70517 -1.717e-05 1.33715 -1.72002e-05C0.970847 -0.00147731 0.61843 0.124102 0.364976 0.348972C0.113252 0.572386 -0.0186853 0.874629 0.0021448 1.18276C0.0212411 1.49087 0.191371 1.77855 0.472609 1.97858L11.5832 10.9486C11.8748 11.1852 12.2793 11.2961 12.6803 11.2523L12.6808 11.2538Z"
                fill="white"
              />
            </svg>
          </div>
          <div
            className={`mb-6 transition-all ease-out overflow-hidden origin-top transform bg-azulRoyal ${
              showPainel ? "max-h-screen" : "scale-y-0 max-h-0"
            }`}
          >
            <div className="p-4 painel-filtros">
              <div className="">
                Marque abaixo as colunas que vão aparecer na tabela:
              </div>
              <div className="mt-4 space-y-3 painel-colunas">
                <div>
                  <IndeterminateCheckbox
                    {...getToggleHideAllColumnsProps()}
                    id="todos"
                  />
                  <label htmlFor="todos"> Todos</label>
                </div>
                {allColumns.map((column) => (
                  <div key={column.id}>
                    <label>
                      <input
                        type="checkbox"
                        id={column.id}
                        {...column.getToggleHiddenProps()}
                      />{" "}
                      <label htmlFor={column.id}>{column.Header}</label>
                    </label>
                  </div>
                ))}
                <br />
              </div>
              <div className="flex justify-end mt-6">
                <a
                  className="underline cursor-pointer opacity-70 hover:opacity-100"
                  onClick={() => resetarColunas()}
                >
                  Resetar Colunas
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      {state.globalFilter && (
        <div className="mt-6 lg:hidden">
          {rows.length} resultados encontrados{" "}
          <a
            className="font-bold text-verdeTeal"
            href={`${pageLink}#tabela-dados`}
          >
            ver
          </a>
        </div>
      )}
      <div className="sticka">
        <div className="pt-11">
          <b>Busca geral:</b>
          <div className="flex flex-col items-center w-full mb-3 xl:space-x-4 xl:flex-row">
            <GlobalFilter
              globalFilter={state.globalFilter}
              setGlobalFilter={setGlobalFilter}
              // ref={node => { globalFilterInput = node; }}
            />
            <div className="flex items-center justify-between w-full xl:space-x-4 xl:w-auto">
              <a
                className="underline cursor-pointer opacity-70 hover:opacity-100"
                onClick={() => limparTabela()}
              >
                Limpar Filtros
              </a>

              <CSVLink
                data={prepareRows(rows)}
                headers={prepareHeaders()}
                filename="data.csv"
                className="o-btn c-btn c-btn--primary"
                target="_blank"
              >
                Baixar CSV
              </CSVLink>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto syncscroll" name="tabela-sync">
          <table
            {...getTableProps()}
            className="tabela-siac"
            id="tabela-headers"
          >
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps()}
                      className={`table-header-cell header-${column.id}`}
                    >
                      <div
                        className="flex items-center justify-center space-x-2 header-tabela"
                        {...column.getSortByToggleProps()}
                      >
                        <div className="text-center whitespace-nowrap">
                          {column.render("Header")}
                        </div>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <svg
                              className="flex-shrink-0 rotate-180 transform-gpu"
                              width="15"
                              height="7"
                              viewBox="0 0 15 7"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7.60848 6.6106C7.77514 6.59001 7.92929 6.52826 8.05011 6.43219L14.7164 1.16215C14.8852 1.04464 14.9872 0.875656 14.9987 0.694674C15.0112 0.513688 14.9321 0.336136 14.781 0.20489C14.6289 0.0727968 14.4175 -0.000971842 14.1977 -0.000113077C13.9769 -0.000113095 13.7675 0.0753688 13.6165 0.208321L7.5 5.046L1.38351 0.20832C1.23248 0.0753677 1.02311 -0.000114165 0.80229 -0.000114183C0.58251 -0.000972032 0.371059 0.0727956 0.218986 0.204889C0.0679518 0.336126 -0.0112108 0.513669 0.00128705 0.694673C0.0127445 0.875659 0.114823 1.04465 0.283566 1.16215L6.94989 6.43131C7.12488 6.57026 7.36758 6.63546 7.60821 6.60972L7.60848 6.6106Z"
                                fill="#ffffff"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="flex-shrink-0"
                              width="15"
                              height="7"
                              viewBox="0 0 15 7"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7.60848 6.6106C7.77514 6.59001 7.92929 6.52826 8.05011 6.43219L14.7164 1.16215C14.8852 1.04464 14.9872 0.875656 14.9987 0.694674C15.0112 0.513688 14.9321 0.336136 14.781 0.20489C14.6289 0.0727968 14.4175 -0.000971842 14.1977 -0.000113077C13.9769 -0.000113095 13.7675 0.0753688 13.6165 0.208321L7.5 5.046L1.38351 0.20832C1.23248 0.0753677 1.02311 -0.000114165 0.80229 -0.000114183C0.58251 -0.000972032 0.371059 0.0727956 0.218986 0.204889C0.0679518 0.336126 -0.0112108 0.513669 0.00128705 0.694673C0.0127445 0.875659 0.114823 1.04465 0.283566 1.16215L6.94989 6.43131C7.12488 6.57026 7.36758 6.63546 7.60821 6.60972L7.60848 6.6106Z"
                                fill="#ffffff"
                              />
                            </svg>
                          )
                        ) : (
                          ""
                        )}
                      </div>
                      <div>
                        {column.canFilter ? column.render("Filter") : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
          </table>
        </div>
      </div>
      <div className="overflow-x-auto syncscroll" name="tabela-sync">
        <table className="tabela-siac" name="tabela-dados" id="tabela-dados">
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps()}
                        data-label={cell.column.Header}
                        className={`column-${cell.column.id}`}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ReactPaginate
        previousLabel={
          <ArrowSVG className="rotate-180 cursor-pointer transform-gpu" />
        }
        nextLabel={<ArrowSVG className="cursor-pointer" />}
        breakLabel={"..."}
        breakClassName={"break-me"}
        pageCount={pageCount}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5}
        pageLinkClassName="cursor-pointer"
        onPageChange={handlePageClick}
        activeClassName={"active text-verdeTeal"}
        containerClassName={
          "pagination flex justify-center items-center space-x-4 mt-8"
        }
      />
      {syncscroll.reset()}
      {/* <div className="flex items-center justify-center mt-8 space-x-4 pagination">
        {canPreviousPage == true && (
          <a className="cursor-pointer" onclick={() => previousPage()}>
            
          </a>
        )}
        {Array.from({ length: pageCount }, (v, k) => k).map((pagina) => {
          return (
            <a
              className={`cursor-pointer ${
                pageIndex + 1 == pagina + 1 ? "text-verdeTeal" : ""
              }`}
              onClick={() => gotoPage(pagina)}
            >
              {pagina + 1}
            </a>
          );
        })}
        {canNextPage == true && (
          <a className="cursor-pointer" onclick={() => nextPage()}>
            <svg
              width="6"
              height="13"
              viewBox="0 0 6 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.9958 6.40599C5.97713 6.26155 5.92111 6.12795 5.83398 6.02324L1.05416 0.245755C0.947574 0.0995131 0.794313 0.0110507 0.630166 0.00111346C0.466014 -0.00971932 0.304978 0.0588883 0.185941 0.189785C0.0661345 0.321584 -0.000771993 0.504843 6.68401e-06 0.695315C6.68629e-06 0.886695 0.0684676 1.06815 0.189053 1.19904L4.57673 6.5L0.189053 11.801C0.0684678 11.9319 6.82017e-06 12.1133 6.82245e-06 12.3047C-0.000770897 12.4952 0.0661346 12.6784 0.185941 12.8102C0.30497 12.9411 0.465999 13.0097 0.630166 12.9989C0.794317 12.989 0.947586 12.9005 1.05416 12.7542L5.83318 6.97676C5.95921 6.8251 6.01834 6.61477 5.995 6.40622L5.9958 6.40599Z"
                fill="#0C2E40"
              />
            </svg>
          </a>
        )}
        <span>
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>{' '}
        </span>
      </div> */}
    </>
  );
}

export default App;
